import React, { useLayoutEffect, useRef } from 'react';

import {
  PaginationCursorRespone,
  PaginationOffsetResponse,
  type TableFilterDescriptor,
  type TableFilterOption,
} from '@fly/shared';
import { useNextEffect } from '@fly/shared-hooks';
import {
  ColumnDef,
  PaginationState,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import cn from 'classnames';
import isEqual from 'lodash/isEqual';

import { FlyTableControls } from '../FlyTableControls';
import { SearchContext } from '../Search/Search';
import {
  CustomRender,
  DataProcessing,
  PaginationType,
  StringKeysOf,
  TANSTACK_TABLE_SCROLL_FADE_GRADIENT_COLOR,
  TANSTACK_TABLE_SCROLL_FADE_GRADIENT_HEIGHT,
  TANSTACK_TABLE_VIRTUAL_SCROLL_VIEWPORT_TEST_ID,
  TableState,
  TableStateType,
  TanstackTableControlsConfig,
  TanstackTableDataConfig,
  TanstackTablePaginationConfig,
  TanstackTableViewConfig,
  TanstackTableVirtualScrollConfig,
} from './TanstackTable.types';
import { useFilterDescriptors } from './components/FilterViews';
import { Pagination } from './components/Pagination/Pagination';
import { getTableViewBody } from './components/getTableViewBody';
import {
  useTableFilteringState,
  useTableFilters,
  useTablePaginationState,
  useTableSortingState,
  useTableStrategy,
} from './hooks';
import { getPaginationDisabled } from './utils/getPaginationDisabled';
import { validateTanstackTableOptions } from './validateTanstackTableOptions';

export function TanstackTable<T extends Record<string, any>>({
  columns,
  data,

  stateType = 'state',
  dataProcessing = 'client',
  paginationType = 'none',
  filters: filtersProp = [],
  filterDescriptors,
  searchKey,
  paginationKey,
  onStateChange,
  loading = false,
  skeletonRows = 5,
  emptyStateElement,
  nextCursor,
  prevCursor,
  serverPagination,
  layout = 'auto',
  enrichSearchControl,

  showSearchSeparator = false,
  fullSearchWidth = true,
  reverseSearchAndFilters = false,
  searchEndSlot,
  searchStartSlot,
  customRender: CustomRenderComponent,
  hideSearch = false,
  hideFilters = false,
  hideTableFragmentIfEmpty = false,
  promptFilter,
  onFilteredCountChange,
  onFilteredDataChange,

  getRowId,

  rowHeight,
  loadMoreSkeletonRows,
  onLoadMore,
  loadingMore,

  virtualScrollViewportTestId = TANSTACK_TABLE_VIRTUAL_SCROLL_VIEWPORT_TEST_ID,
  filterEmptyStateTestId,

  scrollFade = true,
  scrollFadeGradientColor = TANSTACK_TABLE_SCROLL_FADE_GRADIENT_COLOR,
  scrollFadeGradientHeight = TANSTACK_TABLE_SCROLL_FADE_GRADIENT_HEIGHT,

  className,
  ...rest
}: {
  columns: ColumnDef<T>[];
  data: T[];
  stateType?: TableStateType;
  paginationType?: PaginationType;
  /** Legacy: list of filter options. Prefer filterDescriptors for new code. */
  filters?: TableFilterOption<T>[];
  /** Declarative filter config (radio, checkbox, search, custom). When set, used instead of filters. */
  filterDescriptors?: TableFilterDescriptor<T>[];
  searchKey: StringKeysOf<T>;
  paginationKey?: StringKeysOf<T>;
  dataProcessing?: DataProcessing;
  onStateChange?: (state: Partial<TableState>) => void;
  loading?: boolean;
  skeletonRows?: number;
  emptyStateElement?: React.ReactNode;
  nextCursor?: string | null;
  prevCursor?: string | null;
  serverPagination?: PaginationOffsetResponse | PaginationCursorRespone;
  layout?: 'fixed' | 'auto';
  enrichSearchControl?: React.ReactNode;
  searchEndSlot?: React.ReactNode;
  searchStartSlot?: React.ReactNode;
  showSearchSeparator?: boolean;
  reverseSearchAndFilters?: boolean;
  fullSearchWidth?: boolean;
  hideSearch?: boolean;
  hideFilters?: boolean;
  hideTableFragmentIfEmpty?: boolean;
  customRender?: CustomRender<T>;
  /** Custom filter UI; when set, rendered alongside the default filters in FlyTableControls. */
  customFilters?: React.ReactNode;
  /** Prompt filter UI (e.g. FlyFilterByPrompt); when set, rendered in the filter slot. */
  promptFilter?: React.ReactNode;
  /** Called with the number of rows currently shown. Client: after all filters/search. Server: after client-only filters (e.g. envStatus) so display count matches visible rows. */
  onFilteredCountChange?: (count: number) => void;
  /** Called with the filtered row data whenever the visible rows change (after search/filters). */
  onFilteredDataChange?: (data: T[]) => void;
  /** Stable row id (e.g. primary key). Defaults to row index when omitted. */
  getRowId?: (row: T, index: number) => string;
  /** When paginationType='virtual_scroll': estimated row height in px. Default 40. */
  rowHeight?: number;
  /** When paginationType='virtual_scroll': number of skeleton rows shown at bottom while loading next page. Default 5. */
  loadMoreSkeletonRows?: number;
  /** When paginationType='virtual_scroll': called when user scrolls near end or clicks "Load more". Parent should fetch with after=cursor and append to data. */
  onLoadMore?: (cursor: string) => void;
  /** When paginationType='virtual_scroll': true while fetching the next page (shows skeleton rows at bottom). */
  loadingMore?: boolean;
  /** When paginationType='virtual_scroll': `data-testid` on overflow viewport (and passed to CustomRender). Default matches shared automation contract. */
  virtualScrollViewportTestId?: string;
  /** When paginationType='virtual_scroll': optional `data-testid` on filter-empty FlyEmptyState (default table body path). */
  filterEmptyStateTestId?: string;
  /** When `customRender` is set: enable top/bottom scroll fade (default true). */
  scrollFade?: boolean;
  /** When `customRender` is set: fade gradient color (default page background). */
  scrollFadeGradientColor?: string;
  /** When `customRender` is set: fade height in px (default 24). */
  scrollFadeGradientHeight?: number;
  /** Applied to the outer flex wrapper when `customRender` or `virtual_scroll` is active. */
  className?: string;
}) {
  validateTanstackTableOptions({
    dataProcessing,
    paginationType,
    onLoadMore,
  });

  const dataConfig: TanstackTableDataConfig<T> = {
    stateType,
    dataProcessing,
    paginationType,
    data,
    columns,
    serverPagination,
    searchKey,
    paginationKey,
  };
  const controlsConfig: TanstackTableControlsConfig = {
    showSearchSeparator,
    fullSearchWidth,
    reverseSearchAndFilters,
    searchEndSlot,
    searchStartSlot,
    hideSearch,
    hideFilters,
    promptFilter,
    enrichSearchControl,
  };
  const virtualScrollConfig: TanstackTableVirtualScrollConfig = {
    rowHeight,
    loadMoreSkeletonRows,
    onLoadMore,
    loadingMore,
  };
  const viewConfig: TanstackTableViewConfig<T> = {
    customRender: CustomRenderComponent,
    hideTableFragmentIfEmpty,
    emptyStateElement,
    skeletonRows,
    layout,
    scrollFade,
    scrollFadeGradientColor,
    scrollFadeGradientHeight,
  };
  const paginationConfig: TanstackTablePaginationConfig<T> = {
    paginationType,
    dataProcessing,
    paginationKey,
    nextCursor,
    prevCursor,
  };

  const filtersFromDescriptors = useFilterDescriptors(filterDescriptors ?? []);
  const filters =
    filterDescriptors && filterDescriptors.length > 0
      ? filtersFromDescriptors
      : filtersProp;
  const prevStateRef = useRef<TableState | null>(null);
  const { sorting, setSorting } = useTableSortingState(dataConfig.stateType);
  const { pagination, setPagination, resetPagination } =
    useTablePaginationState(dataConfig.stateType, dataConfig.paginationType);
  const { columnFilters, setFilter } = useTableFilteringState(
    dataConfig.stateType,
    dataConfig.searchKey,
    resetPagination
  );

  const tableControls = useTableFilters(
    dataConfig.data,
    filters,
    dataConfig.stateType,
    resetPagination
  );

  const strategyConfig = useTableStrategy({
    dataProcessing: dataConfig.dataProcessing,
    paginationType: dataConfig.paginationType,
    data: dataConfig.data,
    serverPagination: dataConfig.serverPagination,
    paginationState: pagination as PaginationState,
  });

  const table = useReactTable({
    data:
      dataConfig.dataProcessing === 'client'
        ? tableControls.filteredData
        : tableControls.clientFilteredData,
    columns: dataConfig.columns,
    getRowId: getRowId
      ? (originalRow, index) => getRowId(originalRow, index)
      : undefined,
    manualPagination: strategyConfig.manualPagination,
    getCoreRowModel: getCoreRowModel(),
    rowCount: strategyConfig.rowCount,
    pageCount: strategyConfig.pageCount,
    getSortedRowModel: strategyConfig.getSortedRowModel,
    getFilteredRowModel: strategyConfig.getFilteredRowModel,
    getPaginationRowModel: strategyConfig.getPaginationRowModel,
    state: {
      sorting,
      columnFilters,
      pagination: strategyConfig.paginationState,
    },
    onSortingChange: (updaterOrValue) => {
      if (typeof updaterOrValue === 'function') {
        setSorting(updaterOrValue(sorting ?? []));
      } else {
        setSorting(updaterOrValue ?? []);
      }
    },
  });

  // useLayoutEffect so parent state updates (e.g. isSearchPending) apply before
  // the browser paints, preventing a one-frame flash of empty/stale UI.
  useLayoutEffect(() => {
    const currentState = {
      pagination,
      sorting: sorting ?? [],
      columnFilters,
      filters: tableControls.activeFilters,
    };
    if (!isEqual(prevStateRef.current, currentState)) {
      onStateChange?.(currentState);
      prevStateRef.current = currentState;
    }
  }, [
    sorting,
    columnFilters,
    tableControls.activeFilters,
    onStateChange,
    pagination,
  ]);

  const filteredRows = table.getFilteredRowModel().rows;
  const filteredRowCount = filteredRows.length;
  useNextEffect(() => {
    onFilteredCountChange?.(filteredRowCount);
  }, [filteredRowCount, onFilteredCountChange]);

  useNextEffect(() => {
    onFilteredDataChange?.(filteredRows.map((row) => row.original) as T[]);
  }, [filteredRows, onFilteredDataChange]);

  const isDataEmpty = data.length === 0;

  const filterValue = table
    .getColumn(dataConfig.searchKey)
    ?.getFilterValue() as string;
  const isFilterDataEmpty =
    table.getRowCount() === 0 &&
    (tableControls?.filters?.length > 0 || filterValue?.length > 0);

  const { nextDisabled, prevDisabled } = getPaginationDisabled<T>({
    paginationType: paginationConfig.paginationType,
    table,
    nextCursor: paginationConfig.nextCursor,
    prevCursor: paginationConfig.prevCursor,
  });

  const scrollViewportTestIdForVirtual =
    dataConfig.paginationType === 'virtual_scroll'
      ? virtualScrollViewportTestId
      : undefined;

  const body = getTableViewBody({
    table,
    paginationType: dataConfig.paginationType,
    customRender: viewConfig.customRender,
    hideTableFragmentIfEmpty: viewConfig.hideTableFragmentIfEmpty,
    isDataEmpty,
    isFilterDataEmpty,
    loading,
    emptyStateElement: viewConfig.emptyStateElement,
    rowHeight: virtualScrollConfig.rowHeight,
    loadMoreSkeletonRows: virtualScrollConfig.loadMoreSkeletonRows,
    nextCursor: paginationConfig.nextCursor,
    onLoadMore: virtualScrollConfig.onLoadMore,
    loadingMore: virtualScrollConfig.loadingMore,
    skeletonRows: viewConfig.skeletonRows,
    layout: viewConfig.layout,
    scrollViewportTestId: scrollViewportTestIdForVirtual,
    filterEmptyStateTestId,
    scrollFade: viewConfig.scrollFade,
    scrollFadeGradientColor: viewConfig.scrollFadeGradientColor,
    scrollFadeGradientHeight: viewConfig.scrollFadeGradientHeight,
    rest,
  });

  const useFlexWrapper =
    paginationType === 'virtual_scroll' || !!CustomRenderComponent;
  const Wrapper = useFlexWrapper ? 'div' : React.Fragment;
  const wrapperProps = useFlexWrapper
    ? {
        className: cn(
          'flex flex-1 flex-col min-h-0 overflow-hidden',
          className
        ),
      }
    : {};

  return (
    <Wrapper {...wrapperProps}>
      <FlyTableControls
        possibleFilters={filters}
        selectedFilters={tableControls.filters}
        search={filterValue}
        setSearch={setFilter}
        fullSearchWidth={controlsConfig.fullSearchWidth}
        showSeparator={controlsConfig.showSearchSeparator}
        reverseSearchAndFilters={controlsConfig.reverseSearchAndFilters}
        addFilter={tableControls.addFilter}
        removeFilter={tableControls.removeFilter}
        changeFilter={tableControls.changeFilter}
        clearFilters={tableControls.clearFilters}
        hideSearch={controlsConfig.hideSearch}
        promptFilter={controlsConfig.promptFilter}
        hideFilters={controlsConfig.hideFilters}
        endSlot={controlsConfig.searchEndSlot}
        startSlot={controlsConfig.searchStartSlot}
      >
        {!!controlsConfig.enrichSearchControl &&
          controlsConfig.enrichSearchControl}
      </FlyTableControls>
      <SearchContext.Provider
        value={{ search: filterValue, setSearch: setFilter }}
      >
        {body}
      </SearchContext.Provider>
      {paginationConfig.paginationType !== 'virtual_scroll' &&
        (!nextDisabled || !prevDisabled) && (
          <Pagination
            data-testid='pagination'
            table={table}
            dataProcessing={paginationConfig.dataProcessing}
            nextDisabled={nextDisabled}
            prevDisabled={prevDisabled}
            nextCursor={paginationConfig.nextCursor}
            prevCursor={paginationConfig.prevCursor}
            paginationType={paginationConfig.paginationType}
            paginationKey={paginationConfig.paginationKey}
            pagination={pagination}
            setPagination={setPagination}
          />
        )}
    </Wrapper>
  );
}
